import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Phone } from 'lucide-react';
import { CONTACT_EMAIL } from '@/lib/siteConfig';

const policySections = [
  {
    title: 'Information We Collect',
    body: [
      'When you contact S Cube AI Consulting & Solutions, we may receive the details you choose to share, such as your name, email address, company, phone number, preferred consultation details, and message.',
      'Website form submissions are processed by a third-party form delivery service and forwarded to our business email. We do not operate a website database for these submissions.',
    ],
  },
  {
    title: 'How We Use Information',
    body: [
      'We use your information to respond to inquiries, schedule consultations, understand your business needs, and provide relevant information about our AI consulting and solutions services.',
      'We do not sell your personal information.',
    ],
  },
  {
    title: 'Service Providers',
    body: [
      'The website may rely on hosting, domain, email, form delivery, and font services to operate the site and communications. These providers may process limited technical data and inquiry details needed to deliver their services.',
      'If analytics, newsletter tools, or other data-processing services are added later, this policy should be updated before launch of those features.',
    ],
  },
  {
    title: 'Data Retention',
    body: [
      'We retain inquiry emails and related communication only for as long as needed to respond, manage the business relationship, meet legal requirements, or maintain reasonable business records.',
    ],
  },
  {
    title: 'Your Choices',
    body: [
      'You can ask us to update, correct, or delete personal information you have shared with us, subject to applicable legal and business record requirements.',
      'You can also choose not to provide optional information in the contact or consultation forms.',
    ],
  },
  {
    title: 'Security',
    body: [
      'We take reasonable steps to protect information shared with us. However, no website, email system, or internet transmission can be guaranteed to be fully secure.',
    ],
  },
  {
    title: 'Policy Updates',
    body: [
      'We may update this Privacy Policy as the website and services evolve. The updated version will be posted on this page with a revised date.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div>
      <section className="py-24 md:py-32 bg-secondary">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center text-sm text-teal hover:text-teal/80 font-medium mb-8">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Home
          </Link>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">Privacy Policy</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-navy leading-[1.1] mb-6">
            How We Handle Your Information
          </h1>
          <p className="text-base md:text-lg text-navy/60 leading-relaxed">
            This Privacy Policy explains how S Cube AI Consulting & Solutions handles information shared through this website and related email communication.
          </p>
          <p className="text-sm text-navy/45 mt-6">Last updated: December 1, 2025</p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="space-y-12">
            {policySections.map(section => (
              <div key={section.title}>
                <h2 className="font-heading text-2xl md:text-3xl font-medium text-navy mb-4">
                  {section.title}
                </h2>
                <div className="space-y-4">
                  {section.body.map(paragraph => (
                    <p key={paragraph} className="text-navy/60 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 p-8 bg-navy text-white rounded-sm">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">Contact</p>
            <h2 className="font-heading text-2xl font-medium mb-6">Privacy Questions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-white/65 hover:text-white transition-colors">
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                <a href="tel:+917003260866" className="text-sm text-white/65 hover:text-white transition-colors">
                  +91 7003260866
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                <span className="text-sm text-white/65">17, Pratapaditya Place, Kolkata - 700026</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
